import * as UserSessionRepository from "./userSessionRepository";
import { DELIVERYHISTORY_LICENSES_ROUTE, HOME_ROUTE, LOGIN_ROUTE, ROUTES } from "components/router/Routes";
import {
    AUTH_CUSTOM_ASSET_VIEW_CREATE,
    AUTH_CUSTOM_ASSET_VIEW_VIEW,
    AUTH_CUSTOM_REPORT_VIEW_CREATE_OWN,
    AUTH_CUSTOM_REPORT_VIEW_CREATE_SHARED,
    AUTH_CUSTOM_REPORT_VIEW_VIEW,
    AUTH_LICENSE_ASSIGN,
    AUTH_LICENSE_VIEW,
} from "domain/authority";
import { details } from "domain/endpoint";
import { ContractType, LicensingModel, LoginMethod, TenantType } from "domain/tenants";
import { CommonUserDetails, DefaultSettings, Role, UserDetails } from "domain/user";
import { FeatureLicenseType, toFeatureLicenseType } from "domain/users";
import * as aiAgentSessionRepository from "services/ai-agent/aiAgentSessionRepository";
import { apiGatewayService, ApiType } from "services/api/ApiGatewayService";
import * as endpointRepository from "services/login/endpointRepository";
import { authenticationService } from "services/security/AuthenticationService";
import { removeTenantCookie } from "services/tenants/tenantCookieService";
import { TenantLicense } from "services/tenants/TenantService";
import { removeUser } from "store/user";
import { trimToNull } from "utils/commonFunctions";
import { logger } from "utils/logging";

interface UserDetailsDto extends CommonUserDetails {
    tenant_name: string;
    tenant_type: TenantType;
    tenant_uuid: string;
    usage_statistics: boolean;
    erasure_client_endpoint: string;
    eula_accepted_date: string;
    feature_licenses: string[];
    region: string;
    role: Role;
    enabled: boolean;
    tenant_layer: number;
    update_workflow: boolean;
    user_expiration_date: string;
    tenant_expiration_date: string;
    tenant_status: boolean;
    tier: string;
    contract_type: ContractType;
    default_settings: DefaultSettings;
    tenant_licenses: TenantLicense[];
    tenant_login_method?: LoginMethod;
    licensing_model: LicensingModel;
    show_update_old_gen_workflow: boolean;
    show_workflow_updates: boolean;
    workflow_updates: Record<string, string>;
    login_method?: LoginMethod;
    last_login: string;
    total_logins: number;
    ai_user_profile?: string;
}

function deduceDefaultViewPath(savedPath: string | undefined, fallbackPath: string): string {
    if (savedPath == null) {
        return fallbackPath;
    }

    const isExistingPath = ROUTES.some((route) => route.path === savedPath);

    if (isExistingPath) {
        return savedPath;
    }

    logger.warn(`Saved default route path "${savedPath}" no longer exists, falling back to "${fallbackPath}"`);
    return fallbackPath;
}

function toUserDetails(dto: UserDetailsDto): UserDetails {
    const featureLicenses: FeatureLicenseType[] = dto.feature_licenses.map((l) => toFeatureLicenseType(l));
    return {
        created: dto.created,
        email: dto.email,
        modified: dto.modified,
        name: dto.name,
        username: dto.username,
        uuid: dto.uuid,
        tenantName: dto.tenant_name,
        tenantType: dto.tenant_type,
        tenantUuid: dto.tenant_uuid,
        usageStatistics: dto.usage_statistics,
        erasureClientEndpoint: dto.erasure_client_endpoint,
        eulaAcceptedDate: dto.eula_accepted_date,
        featureLicenses: featureLicenses,
        region: dto.region,
        role: dto.role,
        enabled: dto.enabled,
        tenantLayer: dto.tenant_layer,
        userExpirationDate: dto.user_expiration_date,
        tenantExpirationDate: dto.tenant_expiration_date,
        tenantStatus: dto.tenant_status,
        tier: dto.tier,
        contractType: dto.contract_type,
        default_settings: {
            default_view: deduceDefaultViewPath(dto.default_settings?.default_view, HOME_ROUTE.path),
            tenant_access_default_view: deduceDefaultViewPath(
                dto.default_settings?.tenant_access_default_view,
                userSessionService.userHasAllAuthorities([AUTH_LICENSE_VIEW, AUTH_LICENSE_ASSIGN])
                    ? DELIVERYHISTORY_LICENSES_ROUTE.path
                    : HOME_ROUTE.path
            ),
            news_email_opt_in: dto.default_settings?.news_email_opt_in ?? false,
        },
        tenantLoginMethod: dto.tenant_login_method,
        licensingModel: dto.licensing_model,
        showUpdateOldGenWorkflow: dto.show_update_old_gen_workflow,
        showWorkflowUpdates: dto.show_workflow_updates,
        workflowUpdates: dto.workflow_updates,
        loginMethod: dto.login_method,
        lastLogin: dto.last_login,
        totalLogins: dto.total_logins,
        aiUserProfile: trimToNull(dto.ai_user_profile) ?? undefined,
    };
}

class UserSessionService {
    private static removeSession(): void {
        UserSessionRepository.clear();
        endpointRepository.clear();
        aiAgentSessionRepository.clear();
    }

    private setEndpointUrls(response: details): void {
        endpointRepository.setUrl(
            response.message.stan,
            response.message.laurel,
            response.message.arthur,
            response.message.jefferson,
            response.message.oliver,
            response.message.publicApi,
            response.message.publicApiDocumentation
        );
    }

    public async login(username: string, password: string): Promise<UserDetails> {
        try {
            await authenticationService
                .authenticate(username.toLowerCase(), password)
                .then((response) => {
                    this.setEndpointUrls(response);
                })
                .catch((e) => {
                    return Promise.reject(e);
                });

            return await this.fetchUserDetails();
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public async ssoLogin(authorizationCode: string): Promise<UserDetails> {
        try {
            await authenticationService
                .ssoAuthenticate(authorizationCode)
                .then((response) => {
                    this.setEndpointUrls(response);
                })
                .catch((e) => {
                    return Promise.reject(e);
                });

            return await this.fetchUserDetails();
        } catch (e) {
            return Promise.reject(e);
        }
    }

    public storeUser(user: UserDetails) {
        UserSessionRepository.setUser(user);
    }

    public async logout() {
        try {
            UserSessionService.removeSession();
        } catch (e) {
            logger.error("Unable to remove user session", e);
        }

        removeTenantCookie();
        removeUser();

        try {
            await authenticationService.logout();
        } catch (e) {
            logger.error("Authentication service logout failed", e);
        }

        window.location.replace(LOGIN_ROUTE.path);
        return Promise.resolve();
    }

    public currentUserDetails(): UserDetails | null {
        try {
            return UserSessionRepository.getUser();
        } catch (e) {
            logger.error("Unable to get the user details", e);
        }
        return null;
    }

    /**
     * Does the logged in user have a role and authorities within it?
     *
     * @return true when user has role or user isn't logged in. False otherwise.
     */
    public userHasRole(): boolean {
        const user = this.currentUserDetails();
        if (user == null) {
            return true;
        }
        return user.role != null && user.role.authorities != null;
    }

    /**
     * Check if currently logged in user has all authorities.
     *
     * @param authorities If this is empty, it's equivalent to checking that user has authorities in general.
     *
     * @return true if logged in user has provided authorities.
     */
    public userHasAllAuthorities(authorities: string[]): boolean {
        const user = this.currentUserDetails();
        if (user == null) {
            return false;
        }

        const usersAuthorities = new Set(user.role.authorities);

        for (const each of authorities) {
            if (!usersAuthorities.has(each)) {
                return false;
            }
        }

        return true;
    }

    public permittedToCreateCustomViews(type?: string, user?: UserDetails | null): boolean {
        const requiredAuthorities =
            type === "reports"
                ? [AUTH_CUSTOM_REPORT_VIEW_CREATE_OWN, AUTH_CUSTOM_REPORT_VIEW_CREATE_SHARED]
                : [AUTH_CUSTOM_ASSET_VIEW_CREATE];
        return this.hasCustomViewPermission(user?.featureLicenses, requiredAuthorities, type, user);
    }

    public permittedToViewCustomViews(type?: string, user?: UserDetails | null): boolean {
        const requiredAuthorities = type === "reports" ? [AUTH_CUSTOM_REPORT_VIEW_VIEW] : [AUTH_CUSTOM_ASSET_VIEW_VIEW];
        return this.hasCustomViewPermission(user?.featureLicenses, requiredAuthorities, type, user);
    }

    public hasCustomViewPermission(
        featureLicenses: FeatureLicenseType[] | undefined,
        requiredAuthorities: string[],
        type?: string,
        user?: UserDetails | null
    ): boolean {
        if (!featureLicenses?.length) {
            return false;
        }
        const requiredFeatureLicense = type === "reports" ? "FEATURE_CUSTOM_REPORT_VIEWS" : "FEATURE_ASSET_MANAGEMENT";
        const hasAuthority =
            user != null
                ? (() => {
                      const userAuthorities = new Set(user.role.authorities);
                      return requiredAuthorities.some((authority) => userAuthorities.has(authority));
                  })()
                : this.userHasAnyAuthority(requiredAuthorities);
        return featureLicenses.includes(requiredFeatureLicense) && hasAuthority;
    }

    public userHasAnyAuthority(authorities: string[]): boolean {
        const user = this.currentUserDetails();
        if (user == null) {
            return false;
        }

        const usersAuthorities = new Set(user.role.authorities);

        for (const each of authorities) {
            if (usersAuthorities.has(each)) {
                return true;
            }
        }

        return false;
    }

    public fetchUserDetails(): Promise<UserDetails> {
        return apiGatewayService
            .invokeApi("/api/users/profile", "get", null, { apiType: ApiType.LAUREL })
            .then((dto: UserDetailsDto) => toUserDetails(dto))
            .then((user: UserDetails) => {
                return user;
            })
            .catch(() => {
                return Promise.reject("Unable to retrieve user data");
            });
    }

    public hasFeatureLicense(featureLicense: FeatureLicenseType): boolean {
        const currentUser = this.currentUserDetails();
        if (currentUser == null) {
            return false;
        }
        return currentUser.featureLicenses.includes(featureLicense);
    }

    public hasAnyFeatureLicense(featureLicenses: FeatureLicenseType[]): boolean {
        const currentUser = this.currentUserDetails();
        if (currentUser == null) {
            return false;
        }
        return featureLicenses.some((featureLicense) => currentUser.featureLicenses.includes(featureLicense));
    }
}

export const userSessionService = new UserSessionService();
